import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch as SwitchToggle } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Coins, CreditCard, Loader2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { usePaymentConfig } from "@/hooks/use-payment-config";

/**
 * A single payment-method toggle row, styled to match the bordered
 * settings rows used elsewhere in the admin panel (e.g. Security Settings).
 */
function MethodRow({
  Icon,
  title,
  description,
  enabled,
  onToggle,
  ariaLabel,
}: {
  Icon: LucideIcon;
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <div className="rounded-[8px] border border-[var(--ui-border-color)] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Icon
            className={cn(
              "h-6 w-6 shrink-0 mt-0.5",
              enabled ? "text-[#16A34A]" : "text-[var(--secondary-text-color)]"
            )}
          />
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[14px] leading-[16px] font-medium text-[var(--primary-text-color)]">
                {title}
              </span>
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                  enabled
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-700"
                )}
              >
                {enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
            <p className="text-[13px] leading-5 text-[var(--secondary-text-color)] max-w-md">
              {description}
            </p>
          </div>
        </div>

        <div className="shrink-0 pt-0.5">
          <SwitchToggle
            checked={enabled}
            onCheckedChange={onToggle}
            aria-label={ariaLabel}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Admin page to enable/disable site-wide payment methods
 * (SECH Token and/or Credit Card). If card is disabled, users can still
 * remove saved cards but cannot add new ones, and card checkouts are blocked.
 */
export default function PaymentMethodsConfigPage() {
  const { toast } = useToast();
  const { config, isLoading, updateConfig } = usePaymentConfig({ admin: true });

  const [tokenEnabled, setTokenEnabled] = useState<boolean>(true);
  const [cardEnabled, setCardEnabled] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    document.title = "Payment Methods | SECH Admin";
  }, []);

  useEffect(() => {
    setTokenEnabled(config.tokenEnabled);
    setCardEnabled(config.cardEnabled);
  }, [config.tokenEnabled, config.cardEnabled]);

  const hasChanges =
    tokenEnabled !== config.tokenEnabled || cardEnabled !== config.cardEnabled;

  const handleSave = async () => {
    if (!tokenEnabled && !cardEnabled) {
      toast({
        title: "Invalid Configuration",
        description: "At least one payment method must remain enabled.",
        variant: "destructive",
      });
      return;
    }
    setIsSaving(true);
    try {
      await updateConfig({ tokenEnabled, cardEnabled });
      toast({ title: "Payment methods updated successfully" });
    } catch (err) {
      toast({
        title: "Failed to update payment methods",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setTokenEnabled(config.tokenEnabled);
    setCardEnabled(config.cardEnabled);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">
          Payment Methods
        </p>
      </div>

      <div className="p-[16px]">
        <div className="max-w-2xl">
          <Card>
            <CardContent className="p-5 sm:p-6">
              <div className="flex flex-col gap-1 mb-5">
                <h2 className="text-[16px] leading-[20px] font-semibold text-[var(--primary-text-color)]">
                  Accepted Payment Methods
                </h2>
                <p className="text-[13px] leading-5 text-[var(--secondary-text-color)]">
                  Control which payment methods customers can use across
                  memberships, entry passes, and orders. Disabling a method greys
                  it out during checkout and blocks its use everywhere. When card
                  payments are disabled, customers can still remove saved cards
                  but cannot add new ones.
                </p>
              </div>

              {isLoading ? (
                <div className="space-y-3">
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="rounded-[8px] border border-[var(--ui-border-color)] p-4 flex items-start gap-4"
                    >
                      <Skeleton className="h-9 w-9 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-64" />
                      </div>
                      <Skeleton className="h-6 w-11 rounded-full" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  <MethodRow
                    Icon={Coins}
                    title="SECH Token"
                    description="Allow customers to pay from their SECH wallet balance."
                    enabled={tokenEnabled}
                    onToggle={setTokenEnabled}
                    ariaLabel="Toggle SECH token payments"
                  />

                  <MethodRow
                    Icon={CreditCard}
                    title="Credit / Debit Card"
                    description="Allow customers to pay with saved credit or debit cards."
                    enabled={cardEnabled}
                    onToggle={setCardEnabled}
                    ariaLabel="Toggle credit and debit card payments"
                  />

                  {!tokenEnabled && !cardEnabled && (
                    <p className="text-[13px] text-red-600">
                      At least one payment method must remain enabled.
                    </p>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-[36px] text-[13px]"
                      onClick={handleReset}
                      disabled={!hasChanges || isSaving}
                    >
                      Reset
                    </Button>
                    <Button
                      type="button"
                      className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]"
                      onClick={handleSave}
                      disabled={
                        !hasChanges ||
                        isSaving ||
                        (!tokenEnabled && !cardEnabled)
                      }
                    >
                      {isSaving && (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
